import { ReactNode, useState } from 'react'
import { Sidebar, MobileMenuButton, NavigationItem, NavigationSection, ExpandableNavigationItem, BrandingConfig } from './Sidebar'
import { Navbar, NavbarProps } from './Navbar'

export interface DashboardLayoutProps {
  children: ReactNode
  branding: BrandingConfig
  navigation: NavigationItem[]
  documentSections?: NavigationSection[]
  expandableItems?: ExpandableNavigationItem[]
  bottomNavigation?: NavigationItem[]
  hideBottomNavigation?: boolean
  /** Arbitrary content rendered at the bottom of the sidebar (below
   *  bottomNavigation). Forwarded to `Sidebar.footerSlot`. */
  sidebarFooterSlot?: ReactNode
  navbarProps?: Omit<NavbarProps, 'onSearch'> & { onSearch?: (query: string) => void }
  showNavbar?: boolean
  rightPanel?: ReactNode
  /** If true, sidebar is always collapsed and toggle button is hidden */
  alwaysCollapsed?: boolean
  /** If true, sidebar starts collapsed (user can still toggle). Overrides localStorage. */
  defaultCollapsed?: boolean
  /** If true, children are rendered without padding wrapper (for full-height layouts with fixed footers) */
  noPadding?: boolean
  /** Custom className for the content wrapper (when noPadding is false). Defaults to basic mobile header offset. */
  contentClassName?: string
  /** Draw a rule between the flat nav items and the expandable groups. Forwarded to `Sidebar`. */
  dividerBeforeExpandable?: boolean
}

export function DashboardLayout({ 
  children, 
  branding,
  navigation,
  documentSections = [],
  expandableItems = [],
  bottomNavigation = [],
  hideBottomNavigation = false,
  sidebarFooterSlot,
  navbarProps,
  showNavbar = true,
  rightPanel,
  alwaysCollapsed = false,
  defaultCollapsed,
  noPadding = false,
  contentClassName = 'pt-20 lg:pt-0',
  dividerBeforeExpandable = false
}: DashboardLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <div className="flex h-screen overflow-y-hidden overflow-x-visible">
      {/* Mobile Header with Hamburger - only visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <div className={`w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-3 ${branding.logoClassName || ''}`}>
            <span className="text-primary-foreground font-bold text-sm">{branding.logoText}</span>
          </div>
          <h1 className="text-xl font-bold text-foreground">{branding.name}</h1>
        </div>
        <MobileMenuButton 
          isOpen={isMobileMenuOpen} 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
        />
      </div>

      {/* Sidebar */}
      <div className="relative" data-guide="main-sidebar">
        <Sidebar
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          branding={branding}
          navigation={navigation}
          documentSections={documentSections}
          expandableItems={expandableItems}
          bottomNavigation={bottomNavigation}
          hideBottomNavigation={hideBottomNavigation}
          footerSlot={sidebarFooterSlot}
          alwaysCollapsed={alwaysCollapsed}
          defaultCollapsed={defaultCollapsed}
          dividerBeforeExpandable={dividerBeforeExpandable}
        />
      </div>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-y-hidden overflow-x-visible relative">
        {/* Navbar - Desktop only */}
        {showNavbar && (
          <div className="hidden lg:block flex-shrink-0">
            <Navbar {...navbarProps} branding={branding} />
          </div>
        )}
        
        {/* Content and Panel Flex Container */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Main Content Area */}
          <div className={`flex-1 min-w-0 ${noPadding ? 'flex flex-col min-h-0' : 'overflow-y-auto'}`} data-guide="main-content">
            {noPadding ? (
              <div className="flex-1 flex flex-col min-h-0 pt-20 lg:pt-0">
                {children}
              </div>
            ) : (
              <div className={contentClassName}>
                {children}
              </div>
            )}
          </div>

          {/* Right Panel - Desktop */}
          {rightPanel && (
            <div className="hidden lg:block w-80 flex-shrink-0">
              {rightPanel}
            </div>
          )}
        </div>

        {/* Mobile Slideover Panel */}
        {rightPanel && (
          <div className="lg:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
              onClick={() => {
                // Find the close button in the rightPanel and trigger its click
                const closeButton = document.querySelector('[data-panel-close]') as HTMLButtonElement;
                if (closeButton) closeButton.click();
              }}
            />
            
            {/* Slideover Panel */}
            <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl transform transition-transform duration-300 ease-in-out">
              {rightPanel}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}